import { Tag } from 'antd';
import {
  ReportSortButton,
  ReportSearchFilter,
  HeaderTitle,
} from './ReportHeaderComponents';

// ---------------------------------------------------------------------------
// Column factories
// ---------------------------------------------------------------------------

export function getProjectColumns({ searchParams, updateParams, t, isAdmin }) {
  const cols = [
    {
      title: () => (
        <HeaderTitle title={t('project')}>
          <ReportSearchFilter
            field="projectName"
            title={t('project')}
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: ['project', 'name'],
      key: 'project',
      render: (name, record) => {
        if (!record.project) {
          return (
            <span className="italic text-gray-400">
              {record.autoGenerated ? t('unallocatedTime') : t('noProject')}
            </span>
          );
        }
        return (
          <span>
            <span
              className="mr-2 inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: record.project.color || '#1890ff' }}
            />
            {name}
            {record.project.code ? ` (${record.project.code})` : ''}
          </span>
        );
      },
    },
    isAdmin && {
      title: () => (
        <HeaderTitle title={t('billable')}>
          <ReportSortButton
            field="billableHours"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'billableHours',
      key: 'billableHours',
      align: 'right',
      render: (v) => `${v}h`,
    },
    isAdmin && {
      title: () => (
        <HeaderTitle title={t('nonBillable')}>
          <ReportSortButton
            field="nonBillableHours"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'nonBillableHours',
      key: 'nonBillableHours',
      align: 'right',
      render: (v) => `${v}h`,
    },
    {
      title: () => (
        <HeaderTitle title={t('total')}>
          <ReportSortButton
            field="totalHours"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'totalHours',
      key: 'totalHours',
      align: 'right',
      render: (v) => <strong>{v}h</strong>,
    },
    {
      title: '',
      key: 'bar',
      width: 150,
      render: () => null, // Placeholder — overridden by ReportByProject with maxHours
    },
    isAdmin && {
      title: () => (
        <HeaderTitle title={t('amount')}>
          <ReportSortButton
            field="amount"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (v) =>
        v > 0 ? v.toLocaleString('sr-RS', { minimumFractionDigits: 2 }) : '-',
    },
    {
      title: () => (
        <HeaderTitle title={t('entries')}>
          <ReportSortButton
            field="entryCount"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'entryCount',
      key: 'entryCount',
      align: 'center',
    },
  ];

  return cols.filter(Boolean);
}

export function getEmployeeColumns({ searchParams, updateParams, t }) {
  return [
    {
      title: () => (
        <HeaderTitle title={t('employee')}>
          <ReportSearchFilter
            field="employeeName"
            title={t('employee')}
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      key: 'employee',
      render: (_, record) =>
        `${record.employee?.firstName || ''} ${record.employee?.lastName || ''}`.trim(),
    },
    {
      title: () => (
        <HeaderTitle title={t('billable')}>
          <ReportSortButton
            field="billableHours"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'billableHours',
      key: 'billableHours',
      align: 'right',
      render: (v) => `${v}h`,
    },
    {
      title: () => (
        <HeaderTitle title={t('nonBillable')}>
          <ReportSortButton
            field="nonBillableHours"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'nonBillableHours',
      key: 'nonBillableHours',
      align: 'right',
      render: (v) => `${v}h`,
    },
    {
      title: () => (
        <HeaderTitle title={t('total')}>
          <ReportSortButton
            field="totalHours"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'totalHours',
      key: 'totalHours',
      align: 'right',
      render: (v) => <strong>{v}h</strong>,
    },
    {
      title: () => (
        <HeaderTitle title={t('entries')}>
          <ReportSortButton
            field="entryCount"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'entryCount',
      key: 'entryCount',
      align: 'center',
    },
  ];
}

export function getClientColumns({ searchParams, updateParams, t }) {
  return [
    {
      title: () => (
        <HeaderTitle title={t('client')}>
          <ReportSearchFilter
            field="clientName"
            title={t('client')}
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      key: 'client',
      render: (_, record) => record.client?.name || t('noClient'),
    },
    {
      title: t('projects'),
      key: 'projects',
      render: (_, record) =>
        record.projects?.map((p) => (
          <Tag key={p._id} color={p.color || 'blue'} className="mb-1">
            {p.name}
          </Tag>
        )),
    },
    {
      title: () => (
        <HeaderTitle title={t('billable')}>
          <ReportSortButton
            field="billableHours"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'billableHours',
      key: 'billableHours',
      align: 'right',
      render: (v) => `${v}h`,
    },
    {
      title: () => (
        <HeaderTitle title={t('nonBillable')}>
          <ReportSortButton
            field="nonBillableHours"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'nonBillableHours',
      key: 'nonBillableHours',
      align: 'right',
      render: (v) => `${v ?? 0}h`,
    },
    {
      title: () => (
        <HeaderTitle title={t('total')}>
          <ReportSortButton
            field="totalHours"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'totalHours',
      key: 'totalHours',
      align: 'right',
      render: (v) => <strong>{v}h</strong>,
    },
    {
      title: t('efficiency'),
      dataIndex: 'billableRatio',
      key: 'billableRatio',
      align: 'center',
      width: 120,
      render: (ratio) => {
        const r = ratio ?? 0;
        const color =
          r >= 70 ? 'text-green-600 bg-green-50' :
          r >= 40 ? 'text-amber-600 bg-amber-50' :
          'text-red-600 bg-red-50';
        return (
          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${color}`}>
            {r}%
          </span>
        );
      },
    },
    {
      title: () => (
        <HeaderTitle title={t('amount')}>
          <ReportSortButton
            field="totalAmount"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      render: (v) =>
        v > 0 ? v.toLocaleString('sr-RS', { minimumFractionDigits: 2 }) : '-',
    },
    {
      title: () => (
        <HeaderTitle title={t('entries')}>
          <ReportSortButton
            field="entryCount"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'entryCount',
      key: 'entryCount',
      align: 'center',
    },
  ];
}
